import {Injectable, OnDestroy} from '@angular/core';

import {combineLatest, EMPTY, Observable, ReplaySubject, Subject} from 'rxjs';
import {SmagApiService} from '../api/smag-api.service';
import {Config, ConfigType, EconomicsConfig, TimeConfig} from 'smag-commons/cfg';
import {LoginService} from './login.service';
import {map} from 'rxjs/operators';
import {HierarchyService} from './hierarchy.service';

/**
 * Service that deals with generic configuration of SmartAggregator system.
 */
@Injectable()
export class ConfigurationService implements OnDestroy {

    private destroyed = new Subject<void>();
    private timeCfgSubj: ReplaySubject<TimeConfig>;
    private economicsCfgSubj: ReplaySubject<EconomicsConfig>;

    constructor(private readonly apiService: SmagApiService,
                private readonly loginService: LoginService,
                private readonly hierarchyService: HierarchyService) {
        this.timeCfgSubj = new ReplaySubject<TimeConfig>(1); // like BehaviorSubject but with no need for initial value
        this.economicsCfgSubj = new ReplaySubject<EconomicsConfig>(1); // like BehaviorSubject but with no need for initial value
        // reload is triggered every time a user gets logged-in (different principals may have different permissions)
        loginService.addSetupHook(1, profile => {
            // NOTE: hierarchy is actually not needed here, but we do not want to wait for completion
            // of network loading before setting up configurations
            console.debug(`Retrieving configurations for user ${profile?.username}`);
            return this.reloadConfigurations();
        });
    }

    ngOnDestroy() {
        this.destroyed.next();
        this.destroyed.complete();
    }

    get configurations(): Observable<{ [configType in ConfigType]?: Config }> {
        return combineLatest([
            this.timeCfgSubj,
            this.economicsCfgSubj]).pipe(
            map(([time, economics]) => ({
                [ConfigType.TIME]: time,
                [ConfigType.ECONOMICS]: economics
            }))
        );
    }

    get timeConfig(): Observable<TimeConfig> {
        return this.timeCfgSubj.asObservable();
    }

    get economicsConfig(): Observable<EconomicsConfig> {
        return this.economicsCfgSubj.asObservable();
    }

    config(type: ConfigType): Observable<Config> {
        switch (type) {
            case ConfigType.TIME:
                return this.timeConfig;
            case ConfigType.ECONOMICS:
                return this.economicsConfig;
        }
        return EMPTY;
    }

    reloadTimeConfig(): Promise<TimeConfig> {
        return this.apiService.getConfig(ConfigType.TIME).toPromise()
            .then((cfg: TimeConfig) => {
                this.timeCfgSubj.next(cfg);
                return cfg;
            });
    }

    reloadEconomicsConfig(): Promise<EconomicsConfig> {
        return this.apiService.getConfig(ConfigType.ECONOMICS).toPromise()
            .then((cfg: EconomicsConfig) => {
                this.economicsCfgSubj.next(cfg);
                return cfg;
            });
    }

    reloadConfigurations(): Promise<{ [configType in ConfigType]?: Config }> {
        return Promise.all([this.reloadTimeConfig(), this.reloadEconomicsConfig()])
            .then(([time, economics]: [TimeConfig, EconomicsConfig]) => ({
                [ConfigType.TIME]: time,
                [ConfigType.ECONOMICS]: economics
            }));
    }
}
